import { useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/config";
import { useLanguage } from "@/i18n/LanguageContext";
import { formatApiErrorDetail } from "@/utils/apiError";

export default function ResetPassword() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <KeyRound className="h-5 w-5" />
          </span>
          <h1 className="font-heading text-xl font-bold">{t.admin.resetTitle}</h1>
        </div>
        {done ? (
          <p data-testid="reset-password-success" className="mt-8 rounded-lg bg-emerald-500/10 px-4 py-3 text-sm text-emerald-600">
            {t.admin.resetSuccess}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 grid gap-5" data-testid="reset-password-form">
            {error && (
              <p data-testid="reset-password-error" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}
            {!token && (
              <p data-testid="reset-password-no-token" className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                Invalid or missing reset token.
              </p>
            )}
            <div className="grid gap-2">
              <Label htmlFor="new-password">{t.admin.newPassword}</Label>
              <Input
                id="new-password"
                data-testid="reset-password-input"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button type="submit" data-testid="reset-password-submit-button" disabled={loading || !token} className="gap-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t.admin.resetButton}
            </Button>
          </form>
        )}
        <Link
          to="/admin/login"
          data-testid="reset-back-to-login-link"
          className="mt-6 block text-center text-sm text-muted-foreground hover:text-foreground"
        >
          {t.admin.backToLogin}
        </Link>
      </div>
    </div>
  );
}
